// src/pages/entreprise/recrutement-dirigeants-page.tsx
//
// Page dédiée EXECUTIVE SEARCH — chasse de dirigeants (CEO, DG, DAF, DRH,
// COO, directeurs de BU). Registre PDG-à-PDG, distinct de la page
// /entreprise/recrutement (généraliste cadres, argument volume).
//
// Angle : approche directe, confidentielle, off-market. Zéro annonce,
// zéro candidature entrante, une poignée de profils déjà en poste
// approchés avec discrétion. Aucune référence nommée (pas fournie) →
// preuve méthodologique.

import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Crown,
  ShieldCheck,
  Target,
  Map,
  UserCheck,
  Lock,
  Compass,
  Handshake,
  AlertTriangle,
  Eye,
  RefreshCw,
  FileText,
  ArrowRight,
  Briefcase,
  TrendingUp,
  Building2,
  Award,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import RappelForm from '../../components/forms/RappelForm';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

// ─── Styles partagés (cohérents avec recrutement-page) ───────────────────────
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';
const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';
const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';
const h3Class = 'text-xl font-semibold text-[#30475e] mb-3 leading-snug';
const bodyClass = 'text-base text-neutral-600 leading-relaxed';
const sectionSubtitleClass = 'text-lg md:text-xl text-neutral-600 leading-relaxed';

// ─── Contenus ────────────────────────────────────────────────────────────────

// Ce qui distingue la chasse de dirigeants du recrutement classique
const stakes = [
  {
    icon: Eye,
    title: 'La bonne personne ne cherche pas',
    text: "Votre futur dirigeant est en poste, performant, courtisé. Il ne consulte pas les annonces et ignore les sollicitations de masse. On ne le trouve pas : on va le chercher, un par un.",
  },
  {
    icon: Lock,
    title: 'La confidentialité n’est pas une option',
    text: "Remplacer un membre du comité de direction, préparer une succession, structurer une nouvelle activité : autant d’opérations qui ne doivent pas fuiter — ni en interne, ni sur le marché, ni vers vos concurrents.",
  },
  {
    icon: AlertTriangle,
    title: 'L’erreur se paie au niveau du comité',
    text: "Un mauvais casting à ce niveau, ce n’est pas un poste à repourvoir : c’est une stratégie qui dérape, une équipe déstabilisée, des mois perdus. Le coût réel se compte en trajectoire d’entreprise.",
  },
];

// Parcours du fondateur — la légitimité, de dirigeant à dirigeant
const founderJourney = [
  {
    icon: Briefcase,
    title: '15 ans de recrutement et de chasse',
    text: "Un métier exercé au quotidien : identifier, approcher et convaincre des profils rares. Je connais le marché, les codes, et surtout les dirigeants qui font vraiment la différence.",
  },
  {
    icon: TrendingUp,
    title: 'DRH d’une entreprise en hypercroissance',
    text: "J’ai été de l’autre côté : structurer une organisation qui grandit vite, mettre en place les process, recruter et faire monter les équipes. Je sais ce qu’un dirigeant a besoin de trouver — et ce qui casse quand on se trompe.",
  },
  {
    icon: Building2,
    title: 'Chief Strategy Officer d’un groupe de +100 M€',
    text: "J’ai accompagné la transformation d’une entreprise et constitué toute son équipe dirigeante. J’ai vécu, en interne, l’enjeu de composer un comité de direction qui tient dans la durée.",
  },
];

// La méthode executive en 5 temps
const method = [
  {
    icon: Compass,
    step: '01',
    title: 'Diagnostic d’enjeu',
    text: "Pas une fiche de poste. Un cadrage de dirigeant à dirigeant : pourquoi ce poste, pour quel résultat, dans quel contexte de gouvernance, avec quels critères de réussite mesurables à 12 mois.",
  },
  {
    icon: Map,
    step: '02',
    title: 'Cartographie du marché',
    text: "Nous identifions nommément les profils cibles — dans votre secteur et les secteurs adjacents, en région et au-delà si le poste l’exige. Une short-list construite, pas subie.",
  },
  {
    icon: Target,
    step: '03',
    title: 'Approche directe et discrète',
    text: "Chaque dirigeant ciblé est approché personnellement, sous couvert de confidentialité. On ne pitche pas une offre : on ouvre une conversation et on évalue la réalité de son ouverture au mouvement.",
  },
  {
    icon: UserCheck,
    step: '04',
    title: 'Évaluation de dirigeant',
    text: "Au-delà du CV : posture de leadership, alignement avec votre projet et votre gouvernance, capacité à tenir le poste dans la durée. Vous recevez un dossier argumenté, pas une pile de candidatures.",
  },
  {
    icon: Handshake,
    step: '05',
    title: 'Intégration et engagement',
    text: "Nous accompagnons la prise de poste et sécurisons les 90 premiers jours — le moment où se joue vraiment la réussite d’un recrutement de dirigeant.",
  },
];

// Réassurances calibrées C-level
const guarantees = [
  {
    icon: Lock,
    title: 'Discrétion absolue',
    text: "Mission confidentielle par défaut. Votre nom, votre projet et l’identité des candidats approchés restent protégés à chaque étape.",
  },
  {
    icon: RefreshCw,
    title: 'Garantie replacement 6 mois',
    text: "Si la collaboration se rompt dans les 6 premiers mois, nous relançons une recherche complète, sans frais supplémentaires.",
  },
  {
    icon: FileText,
    title: 'Diagnostic post-mortem honnête',
    text: "Si ça n’a pas fonctionné, nous identifions où — projection, gouvernance, package, contexte — pour ne jamais refaire la même erreur.",
  },
];

// Postes couverts
const roles = [
  'Directeur Général / CEO',
  'Directeur Général Adjoint / COO',
  'Directeur Administratif et Financier / CFO',
  'Directeur des Ressources Humaines / DRH',
  'Directeur Commercial / CRO',
  'Directeur de Business Unit',
  'Directeur Industriel / Opérations',
  'Membre de comité de direction',
];

export default function RecrutementDirigeantsPage() {
  const navigate = useNavigate();

  return (
    <Layout
      title="Recrutement de dirigeants à Marseille"
      description="Chasse de dirigeants à Marseille et PACA : CEO, DG, DAF, DRH, COO. Approche directe off-market, méthode executive en 5 temps, garantie 6 mois."
      keywords="recrutement dirigeant, chasse de tête dirigeant, executive search marseille, recrutement CEO, recrutement DG, recrutement DAF, chasse de tête paca, cabinet executive search"
    >
      <WebPageSchema
        page={{
          title: 'Recrutement de dirigeants · Chasse de tête à Marseille',
          description:
            'Chasse de dirigeants confidentielle : CEO, DG, DAF, DRH, COO, directeurs de BU. Approche directe off-market, méthode executive en 5 temps, garantie replacement 6 mois.',
          url: 'https://recruteurs.io/entreprise/recrutement-dirigeants',
          lastReviewed: '2026-07-27',
        }}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Entreprise', url: 'https://recruteurs.io/entreprise/recrutement' },
          {
            name: 'Recrutement de dirigeants',
            url: 'https://recruteurs.io/entreprise/recrutement-dirigeants',
          },
        ]}
      />

      {/* HERO — message (gauche) + formulaire de rappel (droite) */}
      <SectionBackground
        page="recrutement"
        section="dirigeants-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#e0f2ff] via-[#f3e8ff] to-[#fdf4ff]"
      >
        <section className="relative z-10 py-14 md:py-16 lg:py-20">
          <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 items-center gap-10 px-[5vw] md:grid-cols-2 lg:gap-16 xl:px-[100px]">
            {/* Colonne message */}
            <div className="max-w-2xl">
              <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-500 md:text-sm">
                Chasse de dirigeants · Executive search · Marseille · Aix · PACA
              </p>
              <h1 className="pb-1 font-title text-3xl font-extrabold leading-[1.1] text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254] md:text-4xl lg:text-5xl xl:text-[3.4rem]">
                Le recrutement qui engage l’avenir de votre entreprise.
              </h1>
              <p className="mt-5 mb-6 text-base leading-relaxed text-neutral-800 md:text-lg">
                Vous ne cherchez pas un candidat. Vous cherchez la personne qui va
                porter votre stratégie, tenir votre comité de direction, décider à
                vos côtés. 15 ans de chasse, un passé de DRH en hypercroissance et
                de Chief Strategy Officer d’un groupe de +100 M€ : je mène cette
                recherche par approche directe et confidentielle — de dirigeant à
                dirigeant.
              </p>
              <button
                type="button"
                onClick={() =>
                  document
                    .getElementById('methode')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#d03484] transition hover:gap-2.5"
              >
                Découvrir notre approche →
              </button>
            </div>

            {/* Colonne formulaire */}
            <div className="w-full max-w-md md:justify-self-end">
              <RappelForm />
            </div>
          </div>
        </section>
      </SectionBackground>

      {/* SECTION — Ce qui change quand on recrute un dirigeant */}
      <SectionBackground page="recrutement" section="dirigeants-stake" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>La chasse de dirigeants n’est pas du recrutement</span>
            <h2 className={h2Class}>
              À ce niveau,{' '}
              <span className={gradientText}>tout change.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Une short-list de commerciaux, ça se source. Un dirigeant, ça
              s’approche. Trois différences qui imposent une autre méthode.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stakes.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="rounded-2xl border border-neutral-100 bg-white p-6 shadow-sm"
                >
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#d03484]/10 to-[#d47254]/10">
                    <Icon className="h-6 w-6 text-[#d03484]" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.text}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION — Qui vous accompagne (légitimité fondateur) */}
      <SectionBackground page="recrutement" section="dirigeants-fondateur">
        <Section>
          {/* Bandeau portrait + accroche */}
          <div className="mx-auto mb-14 grid max-w-5xl grid-cols-1 items-center gap-8 md:grid-cols-[280px_1fr] md:gap-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mx-auto w-full max-w-[280px]"
            >
              <div className="overflow-hidden rounded-3xl shadow-xl ring-1 ring-neutral-200">
                <img
                  src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/gerald%20gaillard%20recruteurs.io.png"
                  alt="Gérald Gaillard, fondateur de recruteurs.io — 15 ans de recrutement, ancien DRH et Chief Strategy Officer"
                  width={280}
                  height={350}
                  loading="lazy"
                  decoding="async"
                  className="h-auto w-full object-cover"
                />
              </div>
              <p className="mt-3 text-center text-sm font-semibold text-[#30475e]">
                Gérald Gaillard
                <span className="block text-xs font-normal text-neutral-500">
                  Fondateur · recruteurs.io
                </span>
              </p>
            </motion.div>

            <div className="text-center md:text-left">
              <span className={eyebrowClass}>Qui vous accompagne</span>
              <h2 className={h2Class}>
                Aux côtés des{' '}
                <span className={gradientText}>dirigeants et fondateurs</span>{' '}
                depuis 15 ans.
              </h2>
              <p className={sectionSubtitleClass}>
                Quinze ans à chercher des profils rares pour des dirigeants et des
                fondateurs. Et l’expérience du poste, de l’autre côté : DRH d’une
                entreprise en hypercroissance, puis Chief Strategy Officer d’un
                groupe de plus de 100 M€ où j’ai composé tout un comité de
                direction. Résultat : quand je vous présente un profil, je ne
                coche pas des cases — j’évalue, de l’intérieur, sa capacité à
                réussir dans <em>votre</em> contexte.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {founderJourney.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="rounded-2xl border border-neutral-100 bg-white p-6 shadow-sm"
                >
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#d03484]/10 to-[#d47254]/10">
                    <Icon className="h-6 w-6 text-[#d03484]" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.text}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Nuance structuration / restructuration */}
          <div className="mx-auto mt-10 max-w-4xl rounded-2xl border border-[#d03484]/15 bg-gradient-to-br from-[#d03484]/[0.04] to-[#d47254]/[0.04] p-6 md:p-8">
            <h3 className="mb-3 text-lg font-semibold text-[#30475e]">
              Structurer, organiser ou restructurer : ce ne sont pas les mêmes dirigeants.
            </h3>
            <p className={bodyClass}>
              Un dirigeant qui excelle à <strong>construire</strong> une
              organisation en croissance n’est pas celui qui saura{' '}
              <strong>redresser</strong> une activité sous tension — et
              inversement. Avoir vécu la structuration en hypercroissance comme la
              transformation d’un groupe, c’est savoir lire cette différence dès le
              cadrage, et ne vous présenter que des profils réellement alignés avec
              votre moment d’entreprise.
            </p>
          </div>

          {/* Références */}
          <div className="mx-auto mt-6 flex max-w-4xl items-start gap-3 rounded-2xl border border-neutral-100 bg-white p-5 shadow-sm">
            <Award className="mt-0.5 h-5 w-5 shrink-0 text-[#d03484]" />
            <p className="text-sm leading-relaxed text-neutral-600">
              <strong className="text-[#30475e]">Des références de dirigeants,
              à votre niveau.</strong>{' '}
              Parce qu’une mission de chasse est confidentielle, mes références se
              partagent de vive voix, lors de notre premier échange — pas affichées
              en vitrine.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION — La méthode executive en 5 temps */}
      <SectionBackground page="recrutement" section="dirigeants-methode">
        <Section>
          <div id="methode" className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre méthode</span>
            <h2 className={h2Class}>
              Une recherche menée{' '}
              <span className={gradientText}>comme une opération stratégique.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Cinq temps, du cadrage à l’intégration. Comptez 6 à 10 semaines
              entre le premier échange et la signature.
            </p>
          </div>

          <div className="mx-auto max-w-4xl space-y-4">
            {method.map((m, i) => {
              const Icon = m.icon;
              return (
                <motion.div
                  key={m.step}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="flex items-start gap-5 rounded-2xl border border-neutral-100 bg-white p-6 shadow-sm"
                >
                  <div className="flex shrink-0 flex-col items-center">
                    <span className="text-2xl font-bold text-[#d03484]/30">{m.step}</span>
                    <Icon className="mt-2 h-6 w-6 text-[#d03484]" />
                  </div>
                  <div>
                    <h3 className={h3Class}>{m.title}</h3>
                    <p className={bodyClass}>{m.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION — Postes couverts */}
      <SectionBackground page="recrutement" section="dirigeants-roles" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-10">
            <span className={eyebrowClass}>Postes couverts</span>
            <h2 className={h2Class}>
              Les fonctions{' '}
              <span className={gradientText}>de direction et de comité.</span>
            </h2>
          </div>
          <div className="mx-auto grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-2">
            {roles.map((r) => (
              <div
                key={r}
                className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-white px-4 py-3 shadow-sm"
              >
                <Crown className="h-4 w-4 shrink-0 text-[#d03484]" />
                <span className="text-sm font-medium text-[#30475e]">{r}</span>
              </div>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-center text-sm text-neutral-500">
            Une fonction de direction qui n’apparaît pas ici ? Elle entre
            probablement dans notre périmètre. Parlons-en.
          </p>
        </Section>
      </SectionBackground>

      {/* SECTION — Réassurances */}
      <SectionBackground page="recrutement" section="dirigeants-garantie">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Nos engagements</span>
            <h2 className={h2Class}>
              La confiance{' '}
              <span className={gradientText}>se prouve, elle ne se promet pas.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {guarantees.map((g, i) => {
              const Icon = g.icon;
              return (
                <motion.div
                  key={g.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="rounded-2xl border border-neutral-100 bg-white p-6 shadow-sm"
                >
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#d03484]/10 to-[#d47254]/10">
                    <Icon className="h-6 w-6 text-[#d03484]" />
                  </div>
                  <h3 className={h3Class}>{g.title}</h3>
                  <p className={bodyClass}>{g.text}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* CTA final — registre confidentiel */}
      <SectionBackground page="recrutement" section="dirigeants-cta" className="bg-white">
        <Section>
          <div className="mx-auto max-w-3xl rounded-3xl bg-gradient-to-br from-[#30475e] to-[#3d5a75] p-8 text-center text-white md:p-12">
            <ShieldCheck className="mx-auto mb-4 h-10 w-10 text-white/90" />
            <h2 className="text-2xl font-bold md:text-3xl">
              Un poste de direction à confier ? Parlons-en, en confidentialité.
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-white/80">
              Un premier échange de 30 minutes, sans engagement, pour cadrer
              l’enjeu et vérifier que nous sommes le bon partenaire pour cette
              recherche. Confidentiel par défaut.
            </p>
            <button
              onClick={() => navigate('/reservation')}
              className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:scale-[1.02]"
            >
              Demander un échange confidentiel
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
